/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Joyride, ACTIONS, EVENTS } from 'react-joyride';
import { withUserStore } from '../../contexts/UserStore'
import {
  createPrimerStep,
  sequences
} from './TourSteps'
import { useTourProgress } from '../../utils/tourProgress'

const AccountCreateTour = props => {
  const [stepIndex, setStepIndex] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const saveTimeoutRef = React.useRef(null)
  const lastJoyrideIndexRef = React.useRef(0)
  const updateResumePointLockRef = React.useRef(false)

  // Tour progress management using simple settings
  const tourProgress = useTourProgress(props.userStore, 'AccountCreateTour', 23)

  // Note: Auto-show logic has been moved to TourTriggerDetector in tours.js
  // to allow conditional rendering of tour components

  // Start tour when this component mounts (it's only rendered when active)
  useEffect(() => {
    setIsRunning(true)
    setStepIndex(0) // Always start at welcome step (0) to show resume option

    return () => {
      setIsRunning(false)
    }
  }, [])

  // Helper function to advance to next step manually (from click handlers)
  // Note: Now handled through step.targetClickListener properties

  // Universal target click handler for steps that need click advancement
  const handleTargetClick = () => {
    setTimeout(() => {
      const nextStep = stepIndex + 1
      if (nextStep < steps.length) {
        lastJoyrideIndexRef.current = nextStep
        setStepIndex(nextStep)
      }
    }, 100) // Small delay for UI updates
  }

  // Centralized progress saving function with debouncing
  const saveProgress = (step) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    
    // Debounce the save to avoid rapid-fire updates
    saveTimeoutRef.current = setTimeout(() => {
      if (props.userStore?.currentAccountsUser) {
        tourProgress.setCurrentStep(step)
      }
    }, 100) // Small delay to batch rapid changes
  }


  // Detect account type for primer
  const getAccountType = () => {
    if (!props.userStore?.account) return 'business'
    return props.userStore.account.accountType || 'business'
  }

  // Update resume point when reaching steps marked as resume points
  const updateResumePoint = (step, stepIndex) => {
    if (props.userStore?.currentAccountsUser) {
      // Prevent multiple simultaneous updates with a simple lock
      if (updateResumePointLockRef.current) {
        return
      }

      const currentResumeData = getResumeData()
      const newResumeData = {
        stepIndex: stepIndex,
        name: step.resumePointName || `Step ${stepIndex}`
      }
      
      // Only update if the values have actually changed
      if (!currentResumeData.stepIndex || 
          currentResumeData.stepIndex !== newResumeData.stepIndex || 
          currentResumeData.name !== newResumeData.name) {
        
        // Set lock before making API call
        updateResumePointLockRef.current = true
        
        props.userStore.setAccountsUserSetting('tour-AccountCreateTour-resumeData', JSON.stringify(newResumeData), () => {
          // Release lock after API call completes
          updateResumePointLockRef.current = false
        })
      }
    }
  }

  // Get resume data from single JSON setting
  const getResumeData = () => {
    if (!props.userStore?.currentAccountsUser) return {}
    const resumeDataStr = props.userStore.getAccountsUserSetting('tour-AccountCreateTour-resumeData')
    if (!resumeDataStr) return {}
    
    try {
      const resumeData = JSON.parse(resumeDataStr)
      if (resumeData && resumeData.stepIndex > 0) {
        return resumeData
      }
    } catch {
      // Silently handle parse errors
    }
    
    return {}
  }


  // Simple function to get resume point info (no state, read fresh each time)
  const getResumeInfo = () => {
    const resumeData = getResumeData()
    if (!resumeData.stepIndex) return null
    
    return {
      stepIndex: resumeData.stepIndex,
      name: resumeData.name || `Step ${resumeData.stepIndex}`
    }
  }

  const steps = [
    // Welcome step
    {
      target: 'body',
      content: (
        <div>
          <h4>Welcome to Prioritize.Plus! 🎉</h4>
          <p>Let's get you started by setting up your priorities, strategies, and goals. This will help you align your team on what matters most.</p>
          {tourProgress.canResume ? (
            <div>
              <p>You've made progress through the tour. Choose your option:</p>
              <div className="mt-3">
                <button
                  className="btn btn-primary me-2"
                  onClick={() => {
                    const resumeInfo = getResumeInfo()
                    if (resumeInfo) {
                      // Just update stepIndex - Joyride will handle the transition
                      lastJoyrideIndexRef.current = resumeInfo.stepIndex
                      setStepIndex(resumeInfo.stepIndex)
                    } else {
                      // Fallback to step 1 if no resume point found
                      setStepIndex(1)
                    }
                  }}
                >
                  {(() => {
                    const resumeInfo = getResumeInfo()
                    return `Resume at "${resumeInfo?.name || 'Last checkpoint'}"`
                  })()}
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    tourProgress.reset(() => {
                      // Clear resume data with null values
                      const clearResumeData = { stepIndex: null, name: null }
                      props.userStore.setAccountsUserSetting('tour-AccountCreateTour-resumeData', JSON.stringify(clearResumeData))
                      setStepIndex(1) // Go to next step (framework primer)
                    })
                  }}
                >
                  Start Fresh
                </button>
              </div>
            </div>
          ) : (
            <p>Click "Next" to begin your setup journey!</p>
          )}
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5,
      hideFooter: tourProgress.canResume // Hide default Next button when showing custom buttons
    },
    
    // Framework primer
    { ...createPrimerStep(getAccountType()), resumePoint: true, resumePointName: 'Framework primer' },
    
    // Priority creation sequence
    ...sequences.createPriority(handleTargetClick),
    
    // Strategy creation sequence
    ...sequences.createStrategy(false, handleTargetClick),
    
    // Goal creation sequence
    ...sequences.createGoal(null, handleTargetClick),
    
    // Final completion step
    {
      target: 'body',
      content: (
        <div>
          <h4>Congratulations! 🎉</h4>
          <p>You've successfully set up your account with priorities, strategies, and goals! Here's what you've accomplished:</p>
          <ul>
            <li>✅ <strong>Priorities</strong> - Your guiding principles</li>
            <li>✅ <strong>Strategies</strong> - How you'll uphold your priorities</li>
            <li>✅ <strong>Goals</strong> - Measurable outcomes to track</li>
          </ul>
          <p>You're now ready to align your team and make decisions based on what matters most. Explore the dashboard to see your progress and start collaborating with your team!</p>
          <div className="mt-3">
            <button 
              className="btn btn-primary me-2"
              onClick={() => {
                // Navigate back to dashboard
                document.querySelector('#home-link')?.click()
                props.tourActions.setShowTour(false)
              }}
            >
              Go to Dashboard 🏠
            </button>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => props.tourActions.setShowTour(false)}
            >
              Continue Exploring
            </button>
          </div>
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5
    }
  ]

  // Compute the current target selector for the overlay CSS (React-managed)
  // This must be after steps definition to avoid reference error
  const currentTargetSelector = useMemo(() => {
    if (!isRunning) return ''
    // Never apply blocking overlay during tests unless specifically enabled
    if (window.Cypress && !window.cypressAllowTourOverlay) return ''

    if (stepIndex < steps.length) {
      const step = steps[stepIndex]
      if (step && step.target !== 'body') {
        return step.target
      }
    }
    return ''
  }, [isRunning, stepIndex, steps])

  const joyrideStateChanged = (s) => {
    const { action, index, status, type } = s

    // Target clicks are now handled via DOM event listeners, not Joyride events

    if (status === 'finished' || status === 'skipped' || action === ACTIONS.CLOSE) {
      // Only mark as completed if they actually finished the tour, not if they closed it
      if (status === 'finished' && props.userStore?.currentAccountsUser) {
        tourProgress.setCompleted()
        // Clear resume data on completion
        const clearResumeData = { stepIndex: null, name: null }
        props.userStore.setAccountsUserSetting('tour-AccountCreateTour-resumeData', JSON.stringify(clearResumeData))
      }

      props.tourActions.setShowTour(false)
      setIsRunning(false)
      setStepIndex(0)
      lastJoyrideIndexRef.current = 0
      // Note: overlay CSS is now React-managed, no manual cleanup needed
    } else if (action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER) {
      // Handle NEXT button clicks
      if (index === stepIndex) {
        // Joyride is stuck on the same step - force it to advance
        const nextStep = stepIndex + 1
        if (nextStep < steps.length) {
          lastJoyrideIndexRef.current = nextStep
          setStepIndex(nextStep)
        }
      } else if (index > stepIndex) {
        // Normal forward movement
        lastJoyrideIndexRef.current = index
        setStepIndex(index)
      }
    } else if (action === ACTIONS.PREV && type === EVENTS.STEP_AFTER) {
      // Accept legitimate PREV actions (user clicked Previous button)
      if (index < stepIndex) {
        lastJoyrideIndexRef.current = index
        setStepIndex(index)
      }
    }
  }

  // Save progress and check for resume points whenever stepIndex changes while tour is running
  useEffect(() => {
    if (isRunning && stepIndex > 0) {
      saveProgress(stepIndex)
      
      // Check if we've arrived at a resume point
      const currentStep = steps[stepIndex]
      if (currentStep && currentStep.resumePoint) {
        updateResumePoint(currentStep, stepIndex)
      }
    }
  }, [stepIndex, isRunning])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      // Note: overlay CSS is now React-managed, no manual cleanup needed
    }
  }, [])

  // Set up DOM event listeners based on step.targetClickListener properties
  useEffect(() => {
    if (!isRunning) return

    const currentStep = steps[stepIndex]
    if (!currentStep || !currentStep.targetClickListener) {
      return
    }

    const target = currentStep.target
    if (target === 'body') {
      return
    }

    const targetElement = document.querySelector(target)
    if (!targetElement) {
      return
    }

    const clickHandler = () => {
      // Let the original click behavior happen (don't prevent default)
      // Call the step's targetClickListener after a small delay to let the UI update
      setTimeout(() => {
        currentStep.targetClickListener()
      }, 50)
    }

    targetElement.addEventListener('click', clickHandler, false) // Use bubble phase

    return () => {
      targetElement.removeEventListener('click', clickHandler, false)
    }
  }, [isRunning, stepIndex, steps])

  // Generate overlay CSS content based on current target
  const overlayStyles = isRunning && currentTargetSelector !== '' ? `
    body * {
      pointer-events: none !important;
    }
    [data-floating-ui-portal] *,
    .react-joyride__tooltip *,
    .react-joyride__spotlight,
    .react-joyride__overlay,
    .react-joyride *,
    .modal *,
    .modal-body input,
    .modal-footer button${currentTargetSelector ? `,\n    ${currentTargetSelector}` : ''} {
      pointer-events: auto !important;
    }
  ` : null

  return (
    <>
      {/* React-managed blocking overlay style */}
      {overlayStyles && <style>{overlayStyles}</style>}

      <Joyride
        stepIndex={stepIndex}
        steps={steps}
        spotlightPadding={5}
        spotlightClicks={true}
        disableOverlayClose={true}
        disableOverlay={true}
        callback={joyrideStateChanged}
        run={isRunning}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        styles={{
          options: {
            primaryColor: '#007bff',
            width: 400,
            zIndex: 10002,
          },
          spotlight: {
            backgroundColor: 'rgba(0, 0, 0, 0)',
            border: '2px solid #007bff',
          },
          tooltip: {
            fontSize: '14px',
            padding: '20px',
            zIndex: 10003,
          }
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tour'
        }}
      />
    </>
  )
}

AccountCreateTour.propTypes = {
  tourActions: PropTypes.object.isRequired,
  userStore: PropTypes.object
}

export default withUserStore(AccountCreateTour)
